/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import { Appearance, TextInput, ViewStyle } from "react-native"
import { Farm } from "../../../database"
import { FormProps } from "../form"
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps, FormikValues } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { Text } from "../../text/text"
import { Divider, View } from "native-base"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { FC, useCallback, useMemo, useRef } from "react"
import numeral from "numeral"
// import { Button } from "../../button/button"
import { TextField } from "../../field/text-field/text-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { RadioField } from "../../field/radio-field/radio-field"
import { useStores } from "../../../models"
import { FormControl } from "../../form-control/form-control"
import { InputProps } from "../../input/input"
import _ from "lodash"

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

export interface PondCreateMultipleFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    name_prefix?: string
    models?: Array<{
      name?: string
      length?: number
      width?: number
      deep?: number
    }>
  }
}

const PondCreateForm: FC<{ colorScheme?: "light"|"dark", values: any, index: number } & FieldArrayRenderProps> = observer(function PondCreateForm(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    name,
    values,
    index,
    form: { initialValues }
  } = props;

  const input = {
    [`${name}.${index}.name`]: React.useRef<TextInput>(),
    [`${name}.${index}.length`]: React.useRef<TextInput>(),
    [`${name}.${index}.width`]: React.useRef<TextInput>(),
    [`${name}.${index}.deep`]: React.useRef<TextInput>(),
  }

  const nextInput = {
    [`${name}.${index}.name`]: () => input[`${name}.${index}.length`].current && input[`${name}.${index}.length`].current.focus(),
    [`${name}.${index}.length`]: () => input[`${name}.${index}.width`].current ? input[`${name}.${index}.width`].current.focus() : input[`${name}.${index}.deep`].current && input[`${name}.${index}.deep`].current.focus(),
    [`${name}.${index}.width`]: () => input[`${name}.${index}.deep`].current && input[`${name}.${index}.deep`].current.focus(),
    [`${name}.${index}.deep`]: () => true,
  }

  const areaInputProps = useMemo<InputProps>(() => ({
    testID: `jala:id/${name}.${index}.area-input`,
    placeholderTx: "pondForm.areaPlaceholder",
    value: values.type === 'circle'
      ? numeral(Math.PI * Math.pow(values.length, 2)/4).format('0,0.[0]')
      : numeral(values.length * values.width).format('0,0.[0]'),
    style: {
      borderWidth: 0
    }
  }), [index, name, values.length, values.type, values.width])

  return <>
    <TextField
      name={`${name}.${index}.name`}
      txPrefix="pondForm"
      colorScheme={colorScheme}
      input={input}
      nextInput={nextInput}
      formControlProps={{ isRequired: true }}
    />
    <RadioField
      name={`${name}.${index}.type`}
      txPrefix="pondForm"
      colorScheme={colorScheme}
      radioGroupProps={{
        defaultValue: (initialValues.models[index] || {} as any).type || '',
        value: values.type || ''
      }}
      castValue={(value) => value || ''}
      radios={[{
        value: '',
        children: <Text tx="pondProfileForm.square" preset="body2" ml="6px" colorScheme={colorScheme} />
      }, {
        value: 'circle',
        children: <Text tx="pondProfileForm.circle" preset="body2" ml="6px" colorScheme={colorScheme} />
      }]}
      isHorizontal
    />
    {values.type !== 'circle' ? <View flexDirection="row">
      <View flex={1}>
        <NumeralField
          key={`${values.type}-length`}
          name={`${name}.${index}.length`}
          txPrefix="pondForm"
          colorScheme={colorScheme}
          input={input}
          nextInput={nextInput}
          formControlProps={{ isRequired: true }}
        />
      </View>
      <View flex={1}>
        <NumeralField
          name={`${name}.${index}.width`}
          txPrefix="pondForm"
          colorScheme={colorScheme}
          input={input}
          nextInput={nextInput}
          formControlProps={{ isRequired: true }}
        />
      </View>
    </View> : <NumeralField
      key={`${values.type}-diameter`}
      name={`${name}.${index}.length`}
      txPrefix="pondForm"
      colorScheme={colorScheme}
      input={input}
      nextInput={nextInput}
      formControlProps={{
        labelTx: "pondForm.diameter",
        isRequired: true
      }}
    />}
    <View flexDirection="row">
      <View flex={1}>
        <FormControl
          labelTx="pondForm.area"
          colorScheme={colorScheme}
          inputProps={areaInputProps}
          style={FORM_CONTROL}
        />
      </View>
      <View flex={1}>
        <NumeralField
          name={`${name}.${index}.deep`}
          txPrefix="pondForm"
          colorScheme={colorScheme}
          input={input}
          nextInput={nextInput}
          formControlProps={{ isRequired: true }}
        />
      </View>
    </View>
  </>
})

const FormikField: FC<{ colorScheme?: "light"|"dark" } & FormikProps<any>> = observer(function FormikField(props) {
  const { colorScheme, initialValues, values, setFieldValue, setFieldTouched } = props
  const { appStore } = useStores()

  const renderFieldArray = useCallback((props: FieldArrayRenderProps) => {
    const {
      name,
      // push, remove,
      form: { values }
    } = props

    return <>
      {(values.models || []).map((model, index) => <View key={`${values.farm_id}-${name}-${index}`}>
        <PondCreateForm
          colorScheme={colorScheme}
          values={model}
          index={index}
          {...props}
        />
        {/* {values[name].length > 1 && <View alignItems="flex-end" paddingBottom="8px" paddingX="16px"> */}
        {/*   <Button */}
        {/*     testID="jala:id/delete-button" */}
        {/*     tx="delete" */}
        {/*     textPreset="title4" */}
        {/*     preset="link" */}
        {/*     colorScheme={colorScheme} */}
        {/*     textColor={nativeBaseTheme.colors.danger["500"]} */}
        {/*     onPress={() => remove(index)} */}
        {/*   /> */}
        {/* </View>} */}
        <Divider />
      </View>)}
      {/* <View alignItems="flex-end" paddingY="8px" paddingX="16px"> */}
      {/*   <Button */}
      {/*     testID="jala:id/add-button" */}
      {/*     tx="add" */}
      {/*     textPreset="title4" */}
      {/*     preset="link" */}
      {/*     colorScheme={colorScheme} */}
      {/*     onPress={() => push({})} */}
      {/*   /> */}
      {/* </View> */}
    </>
  }, [colorScheme])

  const fetchFarmData = useCallback((model, search) => Promise.resolve(
    appStore.farms.filter((f) => f.name.toUpperCase().includes((search || '').toUpperCase()))
      .map((f) => Farm.hydrate(f))
  ), [appStore])

  const onChangeText = useMemo(() => _.debounce(async (value) => {
    for (let i = 0; i < values.models.length; i++) {
      const m = values.models[i];
      await setFieldValue(`models.${i}.name`, (m.name || '').replace(values.name_prefix || '', value))
    }
    await setFieldValue("name_prefix", value)
    await setFieldTouched("name_prefix")
  }, 500), [setFieldTouched, setFieldValue, values.models, values.name_prefix])

  return <>
    <ModelField
      name="farm_id"
      txPrefix="pondForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true, labelTx: "pondForm.title" }}
      modelPickerProps={{
        Model: Farm,
        isDisabled: !!initialValues.farm_id,
        onValueChange: async (value) => {
          await setFieldValue("models", [])
          await setFieldValue("farm_id", value || null)
          await setFieldTouched("farm_id")
        },
        fetchData: fetchFarmData,
        InputRightElement: !!initialValues.farm_id && <></> || undefined,
        style: { borderWidth: 0, backgroundColor: color.palette.white }
      }}
    />
    <TextField
      name="name_prefix"
      txPrefix="pondForm"
      colorScheme={colorScheme}
      formControlProps={{
        isRequired: true,
        helperTextTx: "pondForm.namePrefixHelper",
        inputProps: {
          defaultValue: initialValues.name_prefix,
          value: undefined,
          onChangeText
        }
      }}
    />
    <View paddingX="16px" paddingY="16px">
      <Text tx="pondForm.formTitle" preset="title3" colorScheme={colorScheme} />
      <Text tx="pondForm.formSubtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? "700" : "300"]} />
    </View>
    <FieldArray
      name="models"
      render={renderFieldArray}
    />
  </>
})

/**
 * Describe your component here
 */
export const PondCreateMultipleForm = observer(function PondCreateMultipleForm(props: PondCreateMultipleFormProps) {
  const {
    initialValues: initialValuesProps = {}, onSubmit:onSubmitProps, colorScheme = "light" || Appearance.getColorScheme(), innerRef: innerRefProps,
    ...rest
  } = props
  const { appStore: { farmStore } } = useStores()

  const modelForm = useRef<FormikProps<FormikValues>>()
  const innerRef = useCallback((node) => {
    modelForm.current = node;
    if (innerRefProps instanceof Function)
      innerRefProps(node);
  }, [innerRefProps])

  const validationSchema = useMemo(() => yup.object({
    farm_id: yup.number().nullable().required().label(translate("pondForm.farmId")),
    models: yup.array().of(yup.object({
      name: yup.lazy((value, options: any) => {
        return yup.string().ascii()
          .notOneOf(
            [
              ...(modelForm.current && modelForm.current.values.models.map(m => m.name).filter((m, i) => i !== options.index) || []),
              ...(farmStore && farmStore.ponds.map((p) => p.name))
            ],
            translate('yup.array.unique')
          )
          .nullable().required()
          .label(translate("pondForm.name"))
      }),
      type: yup.string().oneOf(['square', 'circle']).nullable().label(translate('pondForm.type')),
      length: yup.number().min(0.5).max(500).nullable().required().when('type', {
        is: 'circle',
        then: (schema) => schema.label(translate("pondForm.diameter")),
        otherwise: (schema) => schema.label(translate("pondForm.length"))
      }),
      width: yup.number().min(0.5).max(500).when('type', {
        is: 'circle',
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.nullable().required()
      }).label(translate("pondForm.width")),
      deep: yup.number().min(0.5).max(10).nullable().required().label(translate("pondForm.deep")),
    })).nullable().required(),
  }), [farmStore])

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      models: values.models.map((m) => {
        const model = {
          ...m,
          farm_id: values.farm_id,
        }
        return _.omit(model, ['area'])
      })
    })
  }, [onSubmitProps])

  const initialValues = useMemo<PondCreateMultipleFormProps["initialValues"]>(() => {
    const models = initialValuesProps.models || [];
    return ({
      farm_id: null,
      name_prefix: 'A',
      ...initialValuesProps,
      models: models.length ? models : [{}].map((m, index) => ({
        ...m,
        name: `A${index + 1}`
      }))
    })
  }, [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {(formikProps) => (
        <FormikField colorScheme={colorScheme} {...formikProps} />
      )}
    </Formik>
  )
})

PondCreateMultipleForm.displayName = 'PondCreateMultipleForm'
